/**
 * Navigation store
 * Manages navigation state and remembers last visited section
 */

import { writable, derived } from 'svelte/store';

export type Section = 'library' | 'reader' | 'settings' | 'profile';

export interface NavigationState {
  currentSection: Section;
  lastContentSection: Section; // Last visited library or reader section
  previousRoute: string | null;
  lastReaderRoute: string | null; // Last reader route with bookId
}

const STORAGE_KEY = 'legere:navigation';

/**
 * Load navigation state from localStorage
 */
function loadNavigationState(): NavigationState {
  if (typeof window === 'undefined') {
    return {
      currentSection: 'library',
      lastContentSection: 'library',
      previousRoute: null,
      lastReaderRoute: null,
    };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        currentSection: parsed.currentSection || 'library',
        lastContentSection: parsed.lastContentSection || 'library',
        previousRoute: parsed.previousRoute || null,
        lastReaderRoute: parsed.lastReaderRoute || null,
      };
    }
  } catch (error) {
    console.error('Failed to load navigation state:', error);
  }

  return {
    currentSection: 'library',
    lastContentSection: 'library',
    previousRoute: null,
    lastReaderRoute: null,
  };
}

/**
 * Save navigation state to localStorage
 */
function saveNavigationState(state: NavigationState) {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save navigation state:', error);
  }
}

/**
 * Create navigation store
 */
function createNavigationStore() {
  const { subscribe, set, update } = writable<NavigationState>(loadNavigationState());

  return {
    subscribe,

    /**
     * Navigate to a section
     */
    navigateTo: (section: Section) => {
      update(state => {
        const newState = {
          currentSection: section,
          lastContentSection: section === 'library' || section === 'reader'
            ? section
            : state.lastContentSection,
          previousRoute: state.previousRoute,
          lastReaderRoute: state.lastReaderRoute,
        };
        saveNavigationState(newState);
        return newState;
      });
    },

    /**
     * Update current route
     */
    setRoute: (route: string) => {
      update(state => {
        // Determine section from route
        let section: Section = 'library';
        if (route.startsWith('/reader')) section = 'reader';
        else if (route.startsWith('/settings')) section = 'settings';
        else if (route.startsWith('/profile')) section = 'profile';

        // Save last reader route if we're in reader mode
        const newLastReaderRoute = route.startsWith('/reader')
          ? route
          : state.lastReaderRoute;

        const newState = {
          currentSection: section,
          lastContentSection: section === 'library' || section === 'reader'
            ? section
            : state.lastContentSection,
          previousRoute: route,
          lastReaderRoute: newLastReaderRoute,
        };

        saveNavigationState(newState);
        return newState;
      });
    },

    /**
     * Get initial route for app start
     * Returns library or last content section (reader)
     * Settings and profile are not restored
     */
    getInitialRoute: (): string => {
      const state = loadNavigationState();
      return state.lastContentSection === 'reader' && state.lastReaderRoute
        ? state.lastReaderRoute
        : '/';
    },

    /**
     * Get last reader route for navigation
     * Returns last reader route or null
     */
    getLastReaderRoute: (): string | null => {
      const state = loadNavigationState();
      return state.lastReaderRoute;
    },

    /**
     * Reset to library
     */
    reset: () => {
      const newState: NavigationState = {
        currentSection: 'library',
        lastContentSection: 'library',
        previousRoute: null,
        lastReaderRoute: null,
      };
      saveNavigationState(newState);
      set(newState);
    },
  };
}

export const navigation = createNavigationStore();

/**
 * Derived store for current section
 */
export const currentSection = derived(
  navigation,
  ($nav) => $nav.currentSection
);

/**
 * Derived store for checking if in reader
 */
export const isInReader = derived(
  navigation,
  ($nav) => $nav.currentSection === 'reader'
);
